import { PlusOutlined, SortAscendingOutlined } from "@ant-design/icons";
import { Button, Divider, message } from "antd";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AppContext from "../../../context/app/context";
import ZoneContext from "../../../context/wms/zone/context";
import ZoneContextProvider from "../../../context/wms/zone/provider";
import { useIsMount } from "../../../utils/hooks/is-mount";
import ZoneTable from "../../../components/wms/zone/zone-table";
import SearchFilterCard from "../../../components/general/filter-card";
import Can from "../../../utils/rbac/can";
import PageHeader from "../../../components/general/layout/page-header";
import RefreshBtn from "../../../components/general/table-components/actions/refresh-btn";
import { Permissions } from "../../../utils/Permissions/Permissions";
import ZoneFilter from "../../../components/wms/zone/zone-filter";
import styles from "./style.module.scss";
import { hasRequiredStaticFilters } from "../../../utils/wms/sort-filter-guard";

interface IProps {
  children?: React.ReactNode;
}

const ZoneComponent: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const navigate = useNavigate();

  const [search, setSearch] = useState<string | undefined>(undefined);
  const [sortEnabled, setSortEnabled] = useState(false);

  const { actions, loading, query } = useContext(ZoneContext);

  useEffect(() => {
    actions.setSearch(search);
  }, [search]);

  const isMounted = useIsMount();
  useEffect(() => {
    !isMounted && actions.getData();
  }, [query, direction]);

  const hasSortingScope = hasRequiredStaticFilters(query, ["storeId"]);

  useEffect(() => {
    if (sortEnabled && !hasSortingScope) {
      setSortEnabled(false);
    }
  }, [hasSortingScope, sortEnabled]);

  const handleSortToggle = () => {
    if (sortEnabled) {
      setSortEnabled(false);
      return;
    }

    if (!hasSortingScope) {
      message.warning(t("wms_sort_zones_filter_required"));
      return;
    }

    setSortEnabled(true);
  };

  return (
    <div dir={direction} className={styles.container}>
      <PageHeader
        title={t("zones")}
        subTitle={t("zones_management")}
        extra={[
          <RefreshBtn
            key={0}
            loading={loading.includes("list")}
            onClick={() => actions.getData()}
          >
            {t("refresh")}
          </RefreshBtn>,
          <Can key={3} perform={Permissions.CAN_VIEW_ALL_WMS_ZONES}>
            <Divider type="vertical" />
            <Button
              type={sortEnabled ? "primary" : "default"}
              onClick={handleSortToggle}
              icon={<SortAscendingOutlined />}
            >
              {sortEnabled ? t("disable_sorting") : t("enable_sorting")}
            </Button>
          </Can>,
          <Can key={2} perform={Permissions.CAN_CREATE_WMS_ZONE}>
            <Divider key={1} type="vertical" />
            <Button
              type="primary"
              onClick={() => {
                navigate(`/wms/zones/create`);
              }}
              icon={<PlusOutlined />}
            >
              {t("add_zone")}
            </Button>
          </Can>,
        ]}
      />
      <SearchFilterCard
        formId="zone-filter"
        searchValue={search}
        setSearchValue={setSearch}
        onReset={() => {
          actions.setQuery({
            page: query?.page,
            perPage: query?.perPage,
          });
        }}
        resetLoading={loading.includes("list")}
        applyLoading={loading.includes("list")}
      >
        <ZoneFilter />
      </SearchFilterCard>

      <ZoneTable sortEnabled={sortEnabled} />
    </div>
  );
};

const ZonePage = () => {
  return (
    <ZoneContextProvider>
      <ZoneComponent />
    </ZoneContextProvider>
  );
};

export default ZonePage;
